import { type FormEvent, type ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DigestInterval } from '@katsquare/types';
import { Button, Input, Spinner } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { IconCheck, IconClose } from '../../components/icons';
import {
  getAdminIntegrations,
  testAdminStorage,
  updateAdminEmail,
  updateAdminPayments,
  updateAdminRegistration,
} from '../../lib/api';

/** Digest cadence options (defined locally to avoid value-importing the types barrel). */
const DIGEST_OPTIONS: { value: DigestInterval; label: string }[] = [
  { value: 'off', label: 'Off (instant emails)' },
  { value: '30m', label: 'Every 30 minutes' },
  { value: '1h', label: 'Every hour' },
  { value: '2h', label: 'Every 2 hours' },
  { value: '3h', label: 'Every 3 hours' },
  { value: '6h', label: 'Every 6 hours' },
  { value: '12h', label: 'Every 12 hours' },
  { value: 'daily', label: 'Daily' },
];

export function AdminIntegrationsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const integrations = useQuery({ queryKey: ['admin', 'integrations'], queryFn: getAdminIntegrations });

  const test = useMutation({
    mutationFn: testAdminStorage,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'integrations'] });
      toast(r.connected ? 'Storage is reachable.' : `Storage error: ${r.error ?? 'unknown'}`);
    },
    onError: (e: Error) => toast(e.message),
  });

  const [keyInput, setKeyInput] = useState('');
  const updateEmail = useMutation({
    mutationFn: updateAdminEmail,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'integrations'] }),
    onError: (e: Error) => toast(e.message),
  });

  const updateRegistration = useMutation({
    mutationFn: updateAdminRegistration,
    onSuccess: (_d, open) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'integrations'] });
      toast(open ? 'Registrations reopened.' : 'New registrations stopped.');
    },
    onError: (e: Error) => toast(e.message),
  });

  const [rzpKeyId, setRzpKeyId] = useState('');
  const [rzpSecret, setRzpSecret] = useState('');
  const [rzpWebhook, setRzpWebhook] = useState('');
  const updatePayments = useMutation({
    mutationFn: updateAdminPayments,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'integrations'] });
      setRzpKeyId('');
      setRzpSecret('');
      setRzpWebhook('');
      toast('Payment settings saved.');
    },
    onError: (e: Error) => toast(e.message),
  });

  if (integrations.isPending) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (integrations.isError || !integrations.data) {
    return <p className="text-sm text-red-600">Couldn’t load integrations.</p>;
  }
  const { storage, email, payments, registrationOpen } = integrations.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-neutral-900">Integrations</h1>
        <p className="mt-1 text-sm text-neutral-500">
          External services the platform depends on. Storage secrets come from the deployment
          environment; the email key and activity-email cadence are editable here.
        </p>
      </div>

      {/* Sign-ups */}
      <section className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-3">
          <h2 className="font-semibold text-neutral-900">Sign-ups</h2>
          <span className="text-xs text-neutral-400">New account registrations</span>
          {registrationOpen ? (
            <StatusPill ok label="Open" />
          ) : (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              Closed
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4">
          <p className="min-w-0 flex-1 text-sm text-neutral-600">
            {registrationOpen
              ? 'Anyone can create a new account. Stop this to pause self-serve sign-ups — existing users and invited members are unaffected.'
              : 'New sign-ups are paused. Visitors see a “not currently accepting registrations” message. Invited members can still join.'}
          </p>
          <Button
            variant={registrationOpen ? 'ghost' : undefined}
            onClick={() => updateRegistration.mutate(!registrationOpen)}
            disabled={updateRegistration.isPending}
            className={registrationOpen ? 'border border-neutral-200 text-sm text-red-600' : 'text-sm'}
          >
            {updateRegistration.isPending ? (
              <Spinner />
            ) : registrationOpen ? (
              'Stop registrations'
            ) : (
              'Reopen registrations'
            )}
          </Button>
        </div>
      </section>

      {/* Storage */}
      <section className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-3">
          <h2 className="font-semibold text-neutral-900">Object storage</h2>
          <span className="text-xs text-neutral-400">S3 / MinIO</span>
          <StatusPill ok={storage.connected} />
          <Button
            variant="ghost"
            onClick={() => test.mutate()}
            disabled={test.isPending}
            className="ml-auto border border-neutral-200 text-xs"
          >
            {test.isPending ? <Spinner /> : 'Test connection'}
          </Button>
        </div>
        <dl className="grid gap-x-8 gap-y-3 px-5 py-4 sm:grid-cols-2">
          <Row label="Endpoint" value={storage.endpoint} />
          <Row label="Bucket" value={storage.bucket} />
          <Row label="Region" value={storage.region} />
          <Row label="Path-style" value={storage.forcePathStyle ? 'Enabled' : 'Disabled'} />
          <Row label="Access key" value={<code className="text-neutral-500">{storage.accessKeyMasked}</code>} />
          <Row label="Secret key" value={<span className="text-neutral-400">Managed via environment</span>} />
        </dl>
        {!storage.connected && storage.error && (
          <p className="border-t border-neutral-100 px-5 py-3 text-xs text-red-600">{storage.error}</p>
        )}
      </section>

      {/* Email */}
      <section className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-3">
          <h2 className="font-semibold text-neutral-900">Email</h2>
          <span className="text-xs text-neutral-400">Resend</span>
          {email.provider === 'resend' ? (
            <StatusPill ok label="Configured" />
          ) : (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              Console (dev)
            </span>
          )}
        </div>
        <div className="space-y-5 px-5 py-4">
          <dl className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            <Row label="Provider" value={email.provider === 'resend' ? 'Resend' : 'Console (logs only)'} />
            <Row label="From address" value={email.fromAddress} />
          </dl>

          {/* Resend API key — editable */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-400">Resend API key</label>
            <div className="mb-2 text-sm text-neutral-700">
              {email.apiKeyConfigured ? (
                <>
                  <code className="text-neutral-500">{email.apiKeyMasked}</code>{' '}
                  <span className="text-xs text-neutral-400">
                    ({email.apiKeySource === 'db' ? 'set in dashboard' : 'from environment'})
                  </span>
                </>
              ) : (
                <span className="text-neutral-400">Not set — emails are logged, not sent.</span>
              )}
            </div>
            <form
              className="flex flex-wrap items-center gap-2"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                if (!keyInput.trim()) return;
                updateEmail.mutate(
                  { resendApiKey: keyInput.trim() },
                  { onSuccess: () => { setKeyInput(''); toast('Resend key saved.'); } },
                );
              }}
            >
              <Input
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="re_… paste a new key to update"
                autoComplete="off"
                wrapperClassName="min-w-0 flex-1"
              />
              <Button type="submit" disabled={updateEmail.isPending || !keyInput.trim()} className="text-sm">
                {updateEmail.isPending ? <Spinner /> : 'Save key'}
              </Button>
              {email.apiKeySource === 'db' && (
                <button
                  type="button"
                  onClick={() => updateEmail.mutate({ resendApiKey: '' }, { onSuccess: () => toast('Dashboard key cleared.') })}
                  className="text-xs text-neutral-400 hover:text-red-600"
                >
                  Clear
                </button>
              )}
            </form>
            <p className="mt-1 text-xs text-neutral-400">Stored server-side; only the last 4 characters are ever shown.</p>
          </div>

          {/* Digest cadence */}
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-400">Activity emails</label>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={email.digestInterval}
                onChange={(e) =>
                  updateEmail.mutate(
                    { digestInterval: e.target.value as DigestInterval },
                    { onSuccess: () => toast('Activity-email cadence updated.') },
                  )
                }
                disabled={updateEmail.isPending}
                className="rounded-md border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-neutral-900"
              >
                {DIGEST_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-neutral-400">
                Batches new corrections + PDF uploads and emails all members. “Off” sends instant emails instead.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Payments */}
      <section className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-3">
          <h2 className="font-semibold text-neutral-900">Payments</h2>
          <span className="text-xs text-neutral-400">Razorpay</span>
          {payments.provider === 'razorpay' ? (
            <StatusPill ok label="Configured" />
          ) : (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
              Not set up
            </span>
          )}
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-400">Key ID (public)</label>
            <div className="mb-2 text-sm text-neutral-700">
              {payments.keyId ? (
                <code className="text-neutral-500">{payments.keyId}</code>
              ) : (
                <span className="text-neutral-400">Not set</span>
              )}
            </div>
            <Input value={rzpKeyId} onChange={(e) => setRzpKeyId(e.target.value)} placeholder="rzp_test_… or rzp_live_…" autoComplete="off" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-400">
              Key Secret {payments.keyConfigured && <span className="text-emerald-600">· set</span>}
            </label>
            <Input type="password" value={rzpSecret} onChange={(e) => setRzpSecret(e.target.value)} placeholder="paste to update" autoComplete="off" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-400">
              Webhook Secret {payments.webhookConfigured && <span className="text-emerald-600">· set</span>}
            </label>
            <Input type="password" value={rzpWebhook} onChange={(e) => setRzpWebhook(e.target.value)} placeholder="paste to update" autoComplete="off" />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() =>
                updatePayments.mutate({
                  ...(rzpKeyId.trim() ? { keyId: rzpKeyId.trim() } : {}),
                  ...(rzpSecret.trim() ? { keySecret: rzpSecret.trim() } : {}),
                  ...(rzpWebhook.trim() ? { webhookSecret: rzpWebhook.trim() } : {}),
                })
              }
              disabled={updatePayments.isPending || !(rzpKeyId.trim() || rzpSecret.trim() || rzpWebhook.trim())}
              className="text-sm"
            >
              {updatePayments.isPending ? <Spinner /> : 'Save'}
            </Button>
            <span className="text-xs text-neutral-400">
              Webhook URL: <code>https://draft.katsquare.cloud/api/billing/webhook</code>
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}

function StatusPill({ ok, label }: { ok: boolean; label?: string }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      <IconCheck className="h-3 w-3" />
      {label ?? 'Connected'}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
      <IconClose className="h-3 w-3" />
      {label ?? 'Not connected'}
    </span>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-neutral-400">{label}</dt>
      <dd className="truncate text-sm text-neutral-700">{value}</dd>
    </div>
  );
}
